import { useEffect, useMemo, useRef } from 'react';
import type { VideoScene } from '@/lib/api-client';

interface TranscriptListProps {
  scenes: VideoScene[];
  currentTime: number;
  onSeek: (time: number) => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

export function TranscriptList({
  scenes,
  currentTime,
  onSeek,
}: TranscriptListProps) {
  const activeId = useMemo(() => {
    const hit = scenes.find(
      s => currentTime >= s.start_time && currentTime < s.end_time
    );
    return hit?.scene_id;
  }, [scenes, currentTime]);

  const rowRefs = useRef<Map<string, HTMLLIElement | null>>(new Map());

  // Keep the active cue pinned near the top of the scrollable container
  // as the video plays. We back off as soon as the user scrolls the
  // panel themselves and only re-engage when the active cue leaves the
  // viewport - that way manual scroll-back to read an earlier cue isn't
  // yanked away on the next time tick.
  const userScrolledRef = useRef(false);

  useEffect(() => {
    if (!activeId) return;
    const row = rowRefs.current.get(activeId);
    if (!row) return;

    const scrollContainer = findScrollParent(row);
    if (!scrollContainer) return;

    if (userScrolledRef.current) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const inView =
        rowRect.bottom > containerRect.top &&
        rowRect.top < containerRect.bottom;
      if (inView) return;
      userScrolledRef.current = false;
    }

    // Scroll only this container - using scrollIntoView would chain up
    // and drag the outer page along with us. offsetTop is relative to
    // the offsetParent, which may not be the scroll container, so we
    // diff bounding rects instead.
    const containerRect = scrollContainer.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const target =
      scrollContainer.scrollTop + (rowRect.top - containerRect.top);
    scrollContainer.scrollTo({ top: target, behavior: 'smooth' });
  }, [activeId]);

  if (scenes.length === 0) {
    return (
      <div className='py-8 text-center text-sm text-muted-foreground'>
        Transcript not available for this video.
      </div>
    );
  }

  return (
    <ol
      className='divide-y divide-border'
      onWheel={() => {
        userScrolledRef.current = true;
      }}
      onTouchMove={() => {
        userScrolledRef.current = true;
      }}
    >
      {scenes.map(scene => {
        const isActive = scene.scene_id === activeId;
        return (
          <li
            key={scene.scene_id}
            ref={el => {
              rowRefs.current.set(scene.scene_id, el);
            }}
          >
            <button
              type='button'
              onClick={() => onSeek(scene.start_time)}
              className={`flex w-full items-start gap-3 px-3 py-2 text-left transition-colors ${
                isActive
                  ? 'bg-blue-50 dark:bg-tint-brand-strong'
                  : 'hover:bg-muted dark:hover:bg-gray-800/50'
              }`}
            >
              <span
                className={`min-w-[3rem] shrink-0 font-mono text-xs ${
                  isActive
                    ? 'text-blue-600 dark:text-tint-brand-strong-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                {formatTime(scene.start_time)}
              </span>
              <span
                className={`text-sm leading-relaxed ${
                  isActive ? 'text-foreground' : 'text-foreground'
                }`}
              >
                {scene.title ? (
                  <span className='mr-1 font-medium'>{scene.title}: </span>
                ) : null}
                {scene.narration}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
